import { GetDesignsByDesignValidateApiResponse, ProductVariantResource } from '@/api/serverApi'
import { Button, CollapseIcon, ExpandIcon, FilledCheckIcon } from '@cfs-fixings/shared'
import classNames from 'classnames'
import { useEffect, useMemo, useState } from 'react'
import ProductValidation from './ProductOptionValidations'
import ProductConfiguration from './ProductConfiguration'
import TBoltOptionType from './product-configuration-table/types/TBoltOptionType'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '@/stores'
import { setSelectedProduct } from '@/stores/product-picker'

type PropTypes = {
  product: ProductVariantResource
  selectedLabel: string
  onValidate?: (product: ProductVariantResource) => Promise<GetDesignsByDesignValidateApiResponse | undefined>
  isConfigurable?: boolean
}

const utilisationMapping = {
  tension: [
    'tension_anchor_failure',
    'tension_anchor_channel_connection_failure',
    'tension_local_flexure_of_channel_lips_failure',
    'tension_channel_bolt_failure',
    'tension_flexure_of_channel_failure',
    'tension_pull_out_failure',
    'tension_splitting_failure',
    'tension_blowout_failure',
  ],
  shear: [
    'shear_channel_bolt_failure',
    'shear_anchor_failure',
    'shear_anchor_channel_connection_failure',
    'shear_local_flexure_of_channel_lips_failure',
    'shear_pry_out_failure',
    'shear_concrete_edge_failure',
  ],
  combined: [
    'combined_t_bolt_failure',
    'combined_channel_lips_and_flexural_failure_of_channel',
    'combined_anchor_and_connection_between_anchor_and_channel_failure',
    'combined_concrete_failure',
  ],
}

const ProductOption = ({ product, selectedLabel, onValidate, isConfigurable = false } : PropTypes) => {
  const [isConfiguring, setIsConfiguring] = useState(false)

  const dispatch = useDispatch()

  const selectedProduct = useSelector((state: RootState) => state.productPickerSlice.selectedProduct)

  const [validationResult, setValidationResult] = useState<GetDesignsByDesignValidateApiResponse | undefined>()

  const validationResults = useMemo(() =>
    validationResult?.data?.optimal_configuration?.validation_result ?? validationResult?.data?.validation_to_display,
  [validationResult])

  const tensionPercentage = useMemo(() => {
    if ((validationResults?.length ?? 0) === 0) {
      return null
    }

    const value = utilisationMapping.tension.reduce((acc: number | null, item: string) => {
      const record = validationResults?.find((dataItem) => dataItem.id === item)

      const utilisation = record?.utilisation ? parseFloat(record?.utilisation) : null

      if (record?.error && !utilisation) {
        return null
      }

      if (utilisation! > (acc ?? 0)) {
        return utilisation
      } else {
        return acc
      }
    }, null)

    return value ? value * 100 : null
  }, [validationResults])

  const shearPercentage = useMemo(() => {
    if ((validationResults?.length ?? 0) === 0) {
      return null
    }

    const value = utilisationMapping.shear.reduce((acc: number | null, item: string) => {
      const record = validationResults?.find((dataItem) => dataItem.id === item)

      const utilisation = record?.utilisation ? parseFloat(record?.utilisation) : null

      if (!utilisation) {
        return null
      }

      if (utilisation > (acc ?? 0)) {
        return utilisation
      } else {
        return acc
      }
    }, null)

    return value ? value * 100 : null
  }, [validationResults])

  const combinedPercentage = useMemo(() => {
    if ((validationResults?.length ?? 0) === 0) {
      return null
    }

    const value = utilisationMapping.combined.reduce((acc: number | null, item: string) => {
      const record = validationResults?.find((dataItem) => dataItem.id === item)

      const utilisation = record?.utilisation ? parseFloat(record?.utilisation) : null

      if (!utilisation) {
        return null
      }

      if (utilisation > (acc ?? 0)) {
        return utilisation
      } else {
        return acc
      }
    }, null)

    return value ? value * 100 : null
  }, [validationResults])

  useEffect(() => {
    onValidate?.(product).then((response) => setValidationResult(response))
  }, [onValidate, product])

  const availableOptions = useMemo(() => {
    const productType = product?.product_type
    const compatibleChildren = product?.compatible_children

    switch (productType) {
      case 'cast_in_anchor_channel':
        return compatibleChildren?.reduce((acc: TBoltOptionType[], tBolt) => {
          tBolt?.t_bolt?.diameters.forEach((diameter) => {
            acc.push({
              productVariant: tBolt,
              diameter: diameter,
            })
          })
          return acc
        }, [])
      default:
        return []
    }
  }, [product])

  return (
    <>
      <button
        key={product.id}
        className={
          classNames(
            'border rounded-[5px] pt-4 hover:bg-gray-50 transition-all', {
              'border-gray-60': selectedProduct?.id !== product.id,
              'border-primary-900': selectedProduct?.id === product.id,
            },
          )
        }
        onClick={() => dispatch(setSelectedProduct(product))}
      >
        <div
          className={classNames('px-4 grid grid-flow-row', {
            'grid-rows-2': onValidate,
          })}
        >
          <div
            className={classNames('flex items-center justify-between border-gray-60', {
              'border-b pb-4': onValidate,
            })}
          >
            <div className="text-sm leading-[21px]">
              <span className="font-semibold">{product.name}</span> / {product.product_name}
            </div>
            {
              product?.id === selectedProduct?.id
              && (
                <div className="flex flex-row items-center justify-center">
                  <FilledCheckIcon className="h-5 w-5 mr-2 text-primary-900" />
                  <p className="text-gray-900 text-xs font-medium leading-[18px]">{selectedLabel}</p>
                </div>
              )
            }
          </div>
          <div className="flex flex-row justify-between items-center">
            <div className="w-full pb-4">
              {
                onValidate
                  && (
                    <ProductValidation
                      isLoading={!validationResult?.data}
                      tensionUtilisation={tensionPercentage}
                      shearUtilisation={shearPercentage}
                      combinedUtilisation={combinedPercentage}
                    />
                  )
              }
            </div>
            {
              isConfigurable && validationResult?.data
                && (
                  <Button
                    variant="secondary"
                    size="xs"
                    onClick={(e) => {
                      e.stopPropagation()
                      setIsConfiguring(!isConfiguring)
                    }}
                  >
                    <div className="flex flex-row gap-2 items-center justify-center px-2">
                      {
                        isConfiguring ? <CollapseIcon /> : <ExpandIcon />
                      }
                      {
                        isConfiguring ? 'Collapse' : 'Configure'
                      }
                    </div>
                  </Button>
                )
            }
          </div>
        </div>
        {
          isConfiguring && isConfigurable && validationResult?.data
            && (
              <ProductConfiguration
                parent={product}
                options={availableOptions}
                validationResult={validationResult}
              />
            )
        }
      </button>
    </>
  )
}

export default ProductOption
